import { useCallback, useState } from "react";
import { ApiConfig } from "../Config/CommonConfig";
import { POSTAPI } from "../API/Api";
import type { PresignData } from "../types/cms";

const uploadToS3WithFormData = async (
  presignedUrl: string,
  fields: Record<string, string>,
  file: File
): Promise<boolean> => {
  if (!presignedUrl || !fields || typeof fields !== "object") {
    return false;
  }
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    formData.append(k, v);
  });
  formData.append("file", file);
  const response = await fetch(presignedUrl, {
    method: "POST",
    body: formData,
  });
  return response.ok || response.status === 204 || response.status === 303;
};

export interface CmsUploadDetails {
  fileKey: string;
  publicUrl: string;
  uploadUrl: { url: string; fields: Record<string, string> };
  folder: string;
  originalFileName: string;
  contentType: string;
}

export function useCmsImageUpload() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUploadDetails, setLastUploadDetails] = useState<CmsUploadDetails | null>(
    null
  );

  const clearLastDetails = useCallback(() => setLastUploadDetails(null), []);

  const uploadImage = useCallback(
    async (
      file: File,
      pageSlug: string,
      folderSuffix?: string
    ): Promise<{ fileKey: string; previewUrl: string } | null> => {
      setIsLoading(true);
      setError(null);
      setLastUploadDetails(null);
      try {
        const folder = `public/pages/${folderSuffix ?? pageSlug}`;
        const presignRes = await POSTAPI(
          ApiConfig.CmsPagePresign,
          {
            file_name: file.name,
            content_type: file.type,
            folder,
          },
          false
        );

        if (!presignRes || presignRes.status !== true) {
          const msg =
            presignRes?.message ||
            (presignRes as { errors?: string[] })?.errors?.[0] ||
            "Failed to get upload credentials.";
          setError(msg);
          setIsLoading(false);
          return null;
        }

        const data = presignRes.data as PresignData;
        if (!data?.fileKey || !data?.uploadUrl?.url || !data?.uploadUrl?.fields) {
          setError("Invalid presign response from server.");
          setIsLoading(false);
          return null;
        }

        const ok = await uploadToS3WithFormData(
          data.uploadUrl.url,
          data.uploadUrl.fields,
          file
        );
        if (!ok) {
          setError("Upload to storage failed. Please try again.");
          setIsLoading(false);
          return null;
        }

        setLastUploadDetails({
          fileKey: data.fileKey,
          publicUrl: data.publicUrl || "",
          uploadUrl: {
            url: data.uploadUrl.url,
            fields: data.uploadUrl.fields,
          },
          folder,
          originalFileName: file.name,
          contentType: file.type,
        });

        setIsLoading(false);
        return {
          fileKey: data.fileKey,
          previewUrl: data.publicUrl || "",
        };
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload failed.");
        setIsLoading(false);
        return null;
      }
    },
    []
  );

  const resetError = useCallback(() => setError(null), []);

  return {
    uploadImage,
    isLoading,
    error,
    resetError,
    lastUploadDetails,
    clearLastDetails,
  };
}

export default useCmsImageUpload;
